import React from "react";
import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6 },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.15 },
  },
};

const contactCards = [
  {
    title: "Business Inquiries",
    desc: "Talk to us about cybersecurity solutions, digital systems, and business platform requirements.",
    value: "business@cybershield.com",
    icon: "📩",
  },
  {
    title: "Product Demo",
    desc: "Request a walkthrough of our freight forwarding and secure operations platform.",
    value: "demo@cybershield.com",
    icon: "🖥️",
  },
  {
    title: "Support & Assistance",
    desc: "Reach our team for platform questions, solution guidance, or service-related support.",
    value: "+91 98765 43210",
    icon: "📞",
  },
];

const quickReasons = [
  "Request a product demo",
  "Discuss service requirements",
  "Ask about cybersecurity solutions",
  "Talk about business platform needs",
  "Connect for support or consultation",
  "Explore secure digital transformation",
];

const processSteps = [
  {
    step: "01",
    title: "Share Your Requirement",
    desc: "Tell us about your system, product, business challenge, or security need.",
  },
  {
    step: "02",
    title: "We Review & Respond",
    desc: "Our team reviews the inquiry and responds with the best next step.",
  },
  {
    step: "03",
    title: "Plan the Right Solution",
    desc: "We help identify the right service, product fit, or consultation path.",
  },
];

const Contact = () => {
  return (
    <>
      {/* HERO */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-via-slate-900 tw-to-slate-800 tw-text-white tw-py-5">
        <div className="container">
          <div className="row align-items-center tw-min-h-[65vh]">
            <motion.div
              className="col-lg-7 mb-5 mb-lg-0"
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <motion.span
                variants={fadeUp}
                className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.25em] tw-text-cyan-300 tw-mb-3"
              >
                Contact Our Team
              </motion.span>

              <motion.h1
                className="tw-text-4xl md:tw-text-5xl tw-font-bold tw-leading-tight tw-mb-3"
                variants={fadeUp}
              >
                Let’s Talk About Secure, Reliable, and Scalable Digital Solutions
              </motion.h1>

              <motion.p
                className="tw-text-slate-300 tw-text-lg tw-mb-4"
                variants={fadeUp}
              >
                Whether you need a product demo, cybersecurity guidance, or help
                improving your business platform, our team is ready to connect
                and support your next step.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="tw-flex tw-flex-wrap tw-gap-3"
              >
                <a href="#contact-form" className="btn btn-primary">
                  Send Inquiry
                </a>
                <NavLink to="/services" className="btn btn-outline-light">
                  Explore Services
                </NavLink>
              </motion.div>
            </motion.div>

            <motion.div
              className="col-lg-5"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7 }}
            >
              <div className="tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-3xl tw-p-4 tw-p-lg-5 tw-shadow-lg">
                <h5 className="tw-font-semibold tw-mb-3">Why contact us?</h5>
                <div className="tw-space-y-3">
                  {quickReasons.map((item, i) => (
                    <div
                      key={i}
                      className="tw-bg-white/5 tw-rounded-2xl tw-p-3 tw-text-slate-200"
                    >
                      • {item}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CONTACT CARDS */}
      <section className="container tw-py-5">
        <motion.div
          className="row"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
        >
          {contactCards.map((item, i) => (
            <motion.div key={i} className="col-md-4 mb-4" variants={fadeUp}>
              <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full hover:tw-shadow-lg tw-transition-all">
                <div className="tw-text-4xl tw-mb-3">{item.icon}</div>
                <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                <p className="tw-text-slate-600 tw-text-sm tw-mb-3">{item.desc}</p>
                <div className="tw-text-cyan-700 tw-font-medium">{item.value}</div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* MAIN CONTACT SECTION */}
      <section id="contact-form" className="container tw-py-5">
        <div className="row g-4 align-items-stretch">
          {/* FORM */}
          <motion.div
            className="col-lg-7"
            variants={fadeLeft}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="tw-bg-white tw-p-4 tw-p-lg-5 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
              <div className="tw-mb-4">
                <span className="tw-inline-block tw-text-sm tw-uppercase tw-tracking-[0.2em] tw-text-cyan-600 tw-mb-2">
                  Send Us a Message
                </span>
                <h3 className="tw-font-semibold tw-mb-2">
                  Tell Us What You Need
                </h3>
                <p className="tw-text-slate-600 tw-mb-0">
                  Share your requirement, business need, service inquiry, or
                  product interest, and our team will get back to you.
                </p>
              </div>

              <form>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Full Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Email Address</label>
                    <input
                      type="email"
                      className="form-control"
                      placeholder="Enter your email address"
                    />
                  </div>
                </div>

                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Company Name</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter your company name"
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="form-label">Phone Number</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Enter your phone number"
                    />
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Inquiry Type</label>
                  <select className="form-select">
                    <option>Choose inquiry type</option>
                    <option>Product Demo</option>
                    <option>Cybersecurity Services</option>
                    <option>Business Consultation</option>
                    <option>Support Request</option>
                    <option>General Inquiry</option>
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter your subject"
                  />
                </div>

                <div className="mb-4">
                  <label className="form-label">Message</label>
                  <textarea
                    rows="5"
                    className="form-control"
                    placeholder="Write your message here..."
                  ></textarea>
                </div>

                <button type="submit" className="btn btn-primary px-4">
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>

          {/* INFO */}
          <motion.div
            className="col-lg-5"
            variants={fadeRight}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <div className="tw-bg-slate-50 tw-p-4 tw-p-lg-5 tw-rounded-3xl tw-border tw-border-slate-100 tw-h-full">
              <h4 className="tw-font-semibold tw-mb-3">Contact Information</h4>
              <p className="tw-text-slate-600">
                Reach out for service discussions, security consultations,
                platform inquiries, demos, or support-related questions.
              </p>

              <div className="tw-space-y-4 tw-mt-4">
                <div className="tw-bg-white tw-p-3 tw-rounded-2xl tw-shadow-sm">
                  <h6 className="tw-font-semibold tw-mb-1">Email</h6>
                  <p className="tw-text-slate-600 tw-mb-0">
                    contact@cybershield.com
                  </p>
                </div>

                <div className="tw-bg-white tw-p-3 tw-rounded-2xl tw-shadow-sm">
                  <h6 className="tw-font-semibold tw-mb-1">Phone</h6>
                  <p className="tw-text-slate-600 tw-mb-0">+91 98765 43210</p>
                </div>

                <div className="tw-bg-white tw-p-3 tw-rounded-2xl tw-shadow-sm">
                  <h6 className="tw-font-semibold tw-mb-1">Location</h6>
                  <p className="tw-text-slate-600 tw-mb-0">Chennai, India</p>
                </div>

                <div className="tw-bg-white tw-p-3 tw-rounded-2xl tw-shadow-sm">
                  <h6 className="tw-font-semibold tw-mb-1">Working Hours</h6>
                  <p className="tw-text-slate-600 tw-mb-0">
                    Monday - Friday: 9:00 AM - 6:00 PM
                  </p>
                </div>
              </div>

              <div className="tw-mt-4 tw-p-4 tw-bg-gradient-to-r tw-from-slate-900 tw-to-slate-800 tw-rounded-3xl tw-text-white">
                <h6 className="tw-font-semibold tw-mb-2">Need a faster start?</h6>
                <p className="tw-text-slate-300 tw-text-sm tw-mb-3">
                  Contact us for a demo or consultation and we’ll help guide you
                  to the right solution path.
                </p>
                <NavLink to="/products" className="btn btn-outline-light btn-sm">
                  View Product
                </NavLink>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROCESS / RESPONSE SECTION */}
      <section className="container-fluid tw-bg-slate-100 tw-py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="tw-text-3xl tw-font-semibold">What Happens Next</h2>
            <p className="tw-text-slate-600 tw-max-w-3xl tw-mx-auto">
              We keep the process simple so you can move from inquiry to action
              without unnecessary delay.
            </p>
          </div>

          <motion.div
            className="row text-center"
            variants={stagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
          >
            {processSteps.map((item, i) => (
              <motion.div key={i} className="col-md-4 mb-4" variants={fadeUp}>
                <div className="tw-bg-white tw-p-4 tw-rounded-3xl tw-shadow-sm tw-border tw-border-slate-100 tw-h-full">
                  <div className="tw-text-cyan-600 tw-font-bold tw-text-2xl tw-mb-2">
                    {item.step}
                  </div>
                  <h5 className="tw-font-semibold tw-mb-2">{item.title}</h5>
                  <p className="tw-text-slate-600 tw-text-sm tw-mb-0">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* LOCATION / MAP */}
      <section className="container tw-py-5">
        <div className="text-center mb-4">
          <h2 className="tw-text-3xl tw-font-semibold">Our Location</h2>
          <p className="tw-text-slate-600">
            Based in Chennai, India, supporting businesses with secure digital
            systems and operational technology solutions.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          viewport={{ once: true }}
          className="tw-bg-gradient-to-r tw-from-slate-200 tw-to-slate-100 tw-h-72 tw-rounded-3xl tw-flex tw-items-center tw-justify-center tw-shadow-sm tw-border tw-border-slate-200"
        >
          <div className="text-center">
            <h5 className="tw-font-semibold tw-mb-2">Map Integration Area</h5>
            <p className="tw-text-slate-600 tw-mb-0">
              You can replace this section with Google Maps or your office
              location embed.
            </p>
          </div>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="container-fluid tw-bg-gradient-to-r tw-from-slate-950 tw-to-slate-800 tw-text-white tw-py-5 text-center">
        <div className="container">
          <h2 className="tw-text-3xl tw-font-semibold">
            Ready to Talk About Your Requirements?
          </h2>
          <p className="tw-text-slate-300 tw-max-w-2xl tw-mx-auto">
            Let’s discuss how we can support your business with stronger
            security, better systems, and reliable digital solutions.
          </p>

          <div className="tw-mt-4 tw-flex tw-justify-center tw-gap-3 tw-flex-wrap">
            <a href="#contact-form" className="btn btn-primary">
              Send Inquiry
            </a>
            <NavLink to="/services" className="btn btn-outline-light">
              Explore Services
            </NavLink>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;